import { BucketModel } from "@/database";
import { CREATE_OPTIONS, CREATE_FALLBACK_IDS } from "@/utils/constants/uiConstants";

export type BucketEntry =
  | { kind: "system"; id: string; item: (typeof CREATE_OPTIONS)[number] }
  | { kind: "custom"; id: string; item: BucketModel };

// Always appended regardless of team/user bucket order configuration
const ALWAYS_VISIBLE_IDS = ["BULK_UPLOAD"];

export function resolveBucketEntries(
  createBucketOrder: string[] | null,
  teamBucketOrder: string[] | undefined,
  activeBuckets: BucketModel[]
): BucketEntry[] {
  const resolveId = (id: string): BucketEntry | null => {
    const sys = CREATE_OPTIONS.find((opt) => opt.id === id);
    if (sys) return { kind: "system", id, item: sys };
    const custom = activeBuckets.find((b) => b.id === id);
    if (custom) return { kind: "custom", id, item: custom };
    return null;
  };

  const appendAlways = (entries: BucketEntry[]): BucketEntry[] => {
    const existingIds = new Set(entries.map((e) => e.id));
    const extra = ALWAYS_VISIBLE_IDS
      .map(resolveId)
      .filter((e): e is BucketEntry => !!e && !existingIds.has(e.id));
    return [...entries, ...extra];
  };

  const hasTeamOrder = teamBucketOrder && teamBucketOrder.length > 0;

  // Priority 1: user preference order (team-gated) + new team buckets appended at end
  if (hasTeamOrder && createBucketOrder && createBucketOrder.length > 0) {
    const teamOrderSet = new Set(teamBucketOrder);
    const userOrderSet = new Set(createBucketOrder);

    const userResolved = createBucketOrder
      .filter((id) => teamOrderSet.has(id))
      .map(resolveId)
      .filter(Boolean) as BucketEntry[];

    // New buckets added to team after user saved their preference
    const newTeamEntries = teamBucketOrder!
      .filter((id) => !userOrderSet.has(id))
      .map(resolveId)
      .filter(Boolean) as BucketEntry[];

    const resolved = [...userResolved, ...newTeamEntries];
    if (resolved.length > 0) return appendAlways(resolved);
  }

  // Priority 2: team's bucket_order
  if (hasTeamOrder) {
    const resolved = teamBucketOrder!.map(resolveId).filter(Boolean) as BucketEntry[];
    if (resolved.length > 0) return appendAlways(resolved);
  }

  // Priority 3: fallback — defined in CREATE_FALLBACK_IDS
  const fallback = CREATE_OPTIONS.filter((opt) => CREATE_FALLBACK_IDS.includes(opt.id)).map(
    (opt) => ({ kind: "system" as const, id: opt.id, item: opt })
  );
  return appendAlways(fallback);
}
